import { ice, type Rng } from '@halvard/rules';
import { evaluateWithThreat } from './evaluate.js';
import { searchIce, type IceSearchLimits } from './search.js';

/**
 * - `easy`: the Summit greedy bot. One move ahead, minus the opponent's most dangerous shove.
 * - `normal`, `hard`: the deep bot, 2 and 4 plies.
 * - `expert`: the Summit deep bot, 6 plies (3 moves each), 10 moves at the root and 6 below.
 */
export type IceBotLevel = 'easy' | 'normal' | 'hard' | 'expert';

export const ICE_BOT_LEVELS: readonly { id: IceBotLevel; name: string; detail: string }[] = [
  { id: 'easy', name: 'Easy', detail: 'Looks one move ahead and at your most dangerous shove.' },
  { id: 'normal', name: 'Normal', detail: 'Looks at its move and your reply.' },
  { id: 'hard', name: 'Hard', detail: 'Looks 2 moves ahead for each side.' },
  { id: 'expert', name: 'Expert', detail: 'Looks 3 moves ahead for each side. Slow.' },
];

export const ICE_SEARCH: Readonly<Record<Exclude<IceBotLevel, 'easy'>, IceSearchLimits>> = {
  normal: { depth: 2, rootWidth: 10, width: 8, nodes: 20_000 },
  hard: { depth: 4, rootWidth: 10, width: 6, nodes: 60_000 },
  expert: { depth: 6, rootWidth: 10, width: 6, nodes: 250_000 },
};

/**
 * What a bot may know: everything a player sees, but not the crack seed. Its lookahead draws no
 * new cracks; the cracks already on the ice still thaw on schedule.
 */
export function iceBotView(state: ice.IceState): ice.IceState {
  return { ...state, crackSeed: 0, config: { ...state.config, cracksPerGroan: 0 } };
}

/**
 * Placement, ported from Summit's `botPlace`: a random type among those left, on the front rank
 * of the territory, spread across the three floes, Huscarls a little lower and the others a
 * little higher.
 */
export function chooseIcePlacement(state: ice.IceState, rng: Rng): ice.IceAction {
  const side = state.turn;
  const left = ice.toPlace(state, side);
  const types = ice.WARRIOR_TYPES.filter((t) => left[t] > 0);
  const type = types[Math.floor(rng() * types.length)] ?? 'spear';
  const front = side === 'brand' ? 6 : 2;
  let best: ice.IceAction | null = null;
  let bestScore = -Infinity;
  for (const action of ice.legalIceActions(state)) {
    if (action.kind !== 'place' || action.type !== type) continue;
    const { r, c } = action.at;
    const crowd = state.warriors.filter(
      (w) => w.side === side && Math.floor(w.c / 3) === Math.floor(c / 3),
    ).length;
    const h = state.heights[r]?.[c] ?? 0;
    const score =
      -3 * Math.abs(r - front) - 2 * crowd + h * (type === 'huscarl' ? -0.5 : 1) + rng() * 3;
    if (score > bestScore) {
      bestScore = score;
      best = action;
    }
  }
  if (!best) throw new Error('Nowhere to place');
  return best;
}

/** The Summit greedy bot (`botChoose`). */
export function chooseGreedy(state: ice.IceState, rng: Rng): ice.IceAction {
  const side = state.turn;
  let best: ice.IceAction | null = null;
  let bestScore = -Infinity;
  for (const action of ice.legalIceActions(state)) {
    const child = ice.applyTrustedIceAction(state, action).state;
    const score = evaluateWithThreat(child, side) + rng() * 6;
    if (score > bestScore) {
      bestScore = score;
      best = action;
    }
  }
  if (!best) throw new Error('No legal action: the game is over');
  return best;
}

/** The action a bot of `level` plays for the side to move. Deterministic for a given RNG. */
export function chooseIceAction(state: ice.IceState, level: IceBotLevel, rng: Rng): ice.IceAction {
  const view = iceBotView(state);
  if (view.phase === 'placing') return chooseIcePlacement(view, rng);
  if (level === 'easy') return chooseGreedy(view, rng);
  return searchIce(view, ICE_SEARCH[level], rng);
}
